import { useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { broadcastNotification, BroadcastAudience } from "@/lib/admin";

export default function AdminNotificacionesPage() {
  const { t } = useTranslation("admin");
  const [audience, setAudience] = useState<BroadcastAudience>(BroadcastAudience.Providers);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    setError(null);
    try {
      const { sent } = await broadcastNotification({ audience, title: title.trim(), body: body.trim() });
      setResult(t("admin.notif.sentTo", { count: sent, defaultValue: `Sent to ${sent}` }));
      setTitle("");
      setBody("");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin.notif.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title={t("admin.notif.title")}>
      <div className="max-w-2xl">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <h2 className="text-base font-bold text-charcoal mb-1">{t("admin.notif.send")}</h2>
          <p className="text-sm text-gray-500 mb-5">{t("admin.notif.desc")}</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.notif.recipients")}</label>
              <select
                value={audience}
                onChange={(e) => setAudience(Number(e.target.value) as BroadcastAudience)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean bg-white"
              >
                <option value={BroadcastAudience.Providers}>{t("admin.notif.providers")}</option>
                <option value={BroadcastAudience.Clients}>{t("admin.notif.clients")}</option>
                <option value={BroadcastAudience.All}>{t("admin.notif.allAudience")}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.notif.subject")}</label>
              <input
                type="text"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("admin.notif.subjectPlaceholder")}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.notif.message")}</label>
              <textarea
                value={body}
                maxLength={1000}
                rows={4}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t("admin.notif.messagePlaceholder")}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={send}
              disabled={!canSend}
              className="inline-flex items-center gap-2 bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-ocean/90 disabled:opacity-50"
            >
              <i className="ri-send-plane-line" /> {sending ? t("admin.notif.sending") : t("admin.notif.send")}
            </button>
            {result && <span className="text-xs text-emerald-600">{result}</span>}
            {error && <span className="text-xs text-coral">{error}</span>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
